import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Volume2, Mail, Lock, User, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!username || !email || !password || !confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        try {
            await register(email, password, username);
            toast.success('Account created successfully!');
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || 'Registration failed';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] flex">
            {/* Left Panel - Image */}
            <div className="hidden lg:flex flex-1 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505]" />
                <img 
                    src="https://images.unsplash.com/photo-1690207925026-d774e9571791?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxhdWRpbyUyMHdhdmVmb3JtJTIwdmlzdWFsaXphdGlvbiUyMGFic3RyYWN0fGVufDB8fHx8MTc3MDMwOTQwMXww&ixlib=rb-4.1.0&q=85"
                    alt="Audio visualization"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#D0F224]/10" />
            </div>

            {/* Right Panel - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md space-y-8">
                    <Button
                        variant="ghost"
                        className="text-[#A1A1AA] hover:text-white hover:bg-white/5 -ml-4"
                        onClick={() => navigate('/')}
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Button>

                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-[#D0F224] flex items-center justify-center">
                            <Volume2 className="w-7 h-7 text-black" />
                        </div>
                        <span className="font-heading text-2xl font-bold tracking-tight">AudioMaster Pro</span>
                    </div>

                    <div>
                        <h1 className="font-heading text-3xl font-bold">Create Account</h1>
                        <p className="text-[#A1A1AA] mt-2">Start mastering your audio today</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="username" className="text-white">Username</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                                <Input
                                    id="username"
                                    type="text"
                                    placeholder="producer123"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="pl-10 bg-[#0A0A0A] border-[#333] focus:border-[#D0F224] focus:ring-[#D0F224] text-white placeholder:text-gray-600"
                                    data-testid="username-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-white">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 bg-[#0A0A0A] border-[#333] focus:border-[#D0F224] focus:ring-[#D0F224] text-white placeholder:text-gray-600"
                                    data-testid="email-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-white">Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 bg-[#0A0A0A] border-[#333] focus:border-[#D0F224] focus:ring-[#D0F224] text-white placeholder:text-gray-600"
                                    data-testid="password-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-10 bg-[#0A0A0A] border-[#333] focus:border-[#D0F224] focus:ring-[#D0F224] text-white placeholder:text-gray-600"
                                    data-testid="confirm-password-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full bg-[#D0F224] text-black hover:bg-[#E2F568] font-bold uppercase tracking-wider py-6"
                            disabled={loading}
                            data-testid="register-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Creating Account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </Button>
                    </form>

                    <p className="text-center text-[#A1A1AA]">
                        Already have an account?{' '}
                        <Link 
                            to="/login" 
                            className="text-[#D0F224] hover:underline font-medium"
                            data-testid="login-link"
                        >
                            Sign In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
